import { BugReportOutlined, CloseOutlined, Error, LightbulbOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  FormGroup,
  IconButton,
  Popover,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectAppStatus } from '../redux/reducers/appStatusReducer';
import { sendFeedback } from '../services/craft';
import { AppEnum, CraftCategory, CraftFeedbackType, CraftLabel } from '../Types/enums';
import { AutoCircularProgressWithLabel } from './CircularProgressWithLabel';
import LearnMore from './LearnMore';
import { useAuth } from './Providers/AuthProvider';

interface CraftFeedbackWidgetProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  anchorEl: HTMLElement | null;
  handleClose: () => void;
}

const WidgetHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  p: 1,
});

const WidgetBody = styled(Box)({
  padding: 10,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 20,
});

const ProgressContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  paddingLeft: 20,
  paddingRight: 20,
});

const ToggleContainer = styled(Box)({
  position: 'relative',
  display: 'flex',
  borderRadius: '25px',
  border: '3px solid #070154',
  backgroundColor: '#070154',
  width: 200,
  height: 40,
});

const SelectedButton = styled(Box)(({ selected }: { selected: CraftFeedbackType }) => ({
  position: 'absolute',
  top: 1,
  bottom: 1,
  width: '50%',
  left: selected === CraftFeedbackType.REQUEST ? 0 : '50%',
  borderRadius: '20px',
  backgroundColor: '#fff',
  transition: 'left 0.3s ease',
}));

const ToggleButton = styled(Button)(
  ({ selected, option }: { selected: CraftFeedbackType; option: CraftFeedbackType }) => ({
    flex: 1,
    borderRadius: 0,
    backgroundColor: 'transparent',
    color: selected === option ? '#070154' : '#fff',
    textTransform: 'none',
    fontWeight: 'bold',
    zIndex: 1,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  })
);

const TextArea = styled(TextField)({
  width: 300,
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    borderColor: '#ccc',
    '& fieldset': {
      borderColor: '#ccc',
    },
    '&:hover fieldset': {
      borderColor: '#999',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#070154',
    },
  },
});

const ErrorMessage = styled(Typography)({
  marginTop: 10,
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  fontWeight: 'bold',
});

const SendButton = styled(Button)({
  width: '100%',
  marginTop: 15,
  borderRadius: '5px',
  fontWeight: 'bold',
});

export const CraftFeedbackWidget = (props: CraftFeedbackWidgetProps): JSX.Element => {
  const { currentUser, userFullName } = useAuth();
  const { selectedApp } = useSelector(selectAppStatus);
  const [selected, setSelected] = useState<CraftFeedbackType>(CraftFeedbackType.BUG);
  const [feedbackDescription, setFeedbackDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSend = useCallback(async () => {
    if (!feedbackDescription.trim()) {
      setError('Please enter your feedback before submitting.');
      setTimeout(() => {
        setError(null);
      }, 4000);
      return;
    }

    const feedback = {
      title: `${feedbackDescription.slice(0, 50)}...`,
      description: `${feedbackDescription}\n\n--\nSent by: ${currentUser?.email}`,
      owner: currentUser?.email || userFullName || 'Anonymous',
      categoryId: selectedApp === AppEnum.NIGEL ? CraftCategory.NIGEL : CraftCategory.IA,
      labels: [selected === CraftFeedbackType.BUG ? CraftLabel.BUG : CraftLabel.REQUEST],
    };

    setLoading(true);
    setError(null);

    try {
      await sendFeedback(feedback);

      enqueueSnackbar('Report sent successfully!', { variant: 'success' });
    } catch (err) {
      setLoading(false);
      setError('Error sending your report!');
    }
  }, [feedbackDescription, selected, currentUser, userFullName, selectedApp]);

  const handleOnComplete = useCallback(() => {
    props.handleClose();
    setLoading(false);
    setFeedbackDescription('');
  }, [props]);

  return (
    <Popover
      open={props.open}
      anchorEl={props.anchorEl}
      onClose={props.handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      slotProps={{
        paper: {
          sx: { p: 2, mt: 0.5, borderRadius: '8px', minWidth: 300 },
          role: 'dialog',
          'aria-labelledby': 'report-widget-title',
          'aria-describedby': 'report-widget-description',
        },
      }}
    >
      <Box>
        <WidgetHeader>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            Need to create a Report?
          </Typography>

          <IconButton
            size="small"
            sx={{
              color: '#7c7a7aff',
            }}
            onClick={props.handleClose}
            aria-label="Close report widget"
          >
            <CloseOutlined fontSize="small" />
          </IconButton>
        </WidgetHeader>
        <WidgetBody>
          {loading ? (
            <ProgressContainer>
              <AutoCircularProgressWithLabel
                variant="determinate"
                textColor={'#0047FF'}
                circleColor={'#bbc9d9'}
                size={250}
                thickness={6}
                text={'Sending Report...'}
                config={{ delay: 100, increment: 10 }}
                onComplete={handleOnComplete}
              />
            </ProgressContainer>
          ) : (
            <>
              <ToggleContainer aria-label="Feedback type">
                <SelectedButton selected={selected} />
                {Object.values(CraftFeedbackType).map((option) => (
                  <ToggleButton
                    key={option}
                    onClick={() => setSelected(option as CraftFeedbackType)}
                    selected={selected}
                    option={option as CraftFeedbackType}
                    aria-checked={selected === option}
                    aria-label={`Select ${option.toLowerCase()} type`}
                  >
                    {option === CraftFeedbackType.REQUEST ? (
                      <LightbulbOutlined />
                    ) : (
                      <BugReportOutlined />
                    )}
                    {option}
                  </ToggleButton>
                ))}
              </ToggleContainer>

              {/* Textarea and button */}
              <FormGroup>
                <TextArea
                  value={feedbackDescription}
                  onChange={(e) => setFeedbackDescription(e.target.value)}
                  multiline
                  rows={5}
                  variant="outlined"
                  placeholder="Enter your Report..."
                  aria-label="Report description"
                />

                {error && (
                  <ErrorMessage variant="caption" color="error" aria-live="polite">
                    <Error fontSize="small" aria-hidden="true" />
                    <span style={{ marginTop: 2.5 }}>{error}</span>
                  </ErrorMessage>
                )}

                <SendButton
                  onClick={handleSend}
                  variant="contained"
                  color="primary"
                  aria-label="Send report"
                >
                  Send
                </SendButton>
              </FormGroup>
              <LearnMore
                link="https://westmonroe.ideas.craft.io/WES"
                text="Feedback Portal"
                sx={{ mr: '5px' }}
              />
            </>
          )}
        </WidgetBody>
      </Box>
    </Popover>
  );
};
